import { useState } from 'react';
import { KeyRound, Languages, ShieldCheck, User } from 'lucide-react';
import { NotificationCentre } from './NotificationCentre';
import { getSupabase } from './supabase';

export function PlayerSettingsView({
  language,
  setLanguage,
  email,
  teamName,
  accessLabel = 'Player · Read only',
  eyebrowRole = 'PLAYER',
}: {
  language: 'en' | 'pl';
  setLanguage: (language: 'en' | 'pl') => void;
  email: string;
  teamName: string;
  accessLabel?: string;
  eyebrowRole?: string;
}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function changePassword() {
    if (password.length < 8) {
      setMessage('Password must contain at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const { error } = await getSupabase().auth.updateUser({ password });
      if (error) throw error;
      setPassword('');
      setConfirmPassword('');
      setMessage('Password changed successfully.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Password change failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-page player-settings-page">
      <section className="metal-card settings-hero">
        <div>
          <p className="eyebrow">POLONIA GLASGOW AFC · {eyebrowRole}</p>
          <h1>My Settings</h1>
          <p className="muted">Personal account, language and notifications.</p>
        </div>
        <ShieldCheck size={34}/>
      </section>

      <section className="settings-grid">
        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <Languages size={23}/>
            <div><h2>Language</h2><p>Choose your app language.</p></div>
          </div>
          <div className="segmented-control">
            <button className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>English</button>
            <button className={language === 'pl' ? 'active' : ''} onClick={() => setLanguage('pl')}>Polski</button>
          </div>
        </article>

        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <User size={23}/>
            <div><h2>My Account</h2><p>Read-only player access.</p></div>
          </div>
          <dl className="system-facts">
            <dt>Email</dt><dd>{email || '—'}</dd>
            <dt>Club</dt><dd>{teamName}</dd>
            <dt>Role</dt><dd className="status-good">{accessLabel}</dd>
          </dl>
        </article>

        <NotificationCentre />

        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <KeyRound size={23}/>
            <div><h2>Change Password</h2><p>Replace the temporary password after your first login.</p></div>
          </div>
          <div className="player-password-form">
            <input type="password" autoComplete="new-password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)}/>
            <input type="password" autoComplete="new-password" placeholder="Repeat new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}/>
            <button className="gold-button" type="button" disabled={busy} onClick={() => void changePassword()}>
              <KeyRound size={16}/> {busy ? 'Saving…' : 'Change Password'}
            </button>
            {message && <p className="notification-status">{message}</p>}
          </div>
        </article>
      </section>
    </div>
  );
}
